// src/components/CardsColumn.jsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import { FaEdit, FaTrash, FaTimes, FaFileExport, FaFlag } from "react-icons/fa";
import { supabase } from "../supabaseClient";
import NotaRapidaCard from "./NotaRapidaCard";
import CalendarioDiarioObra from "./RdoCalendario";
import CardPilhaExpand from "./CardPilhaExpand";
import ModalResponsavelPilha from "./ModalResponsavelPilha";
import EnviarNotaModal from "./EnviarNotaModal";
import { podeEnviarNota } from "../utils/solicitacoesEnvio";
import { RdoNotasSkeleton } from "./skeletons/RdoColunaSkeleton";
import "./CardsColumn.css";

// Mapeia tipo → classe de tag colorida
const TAG_CLASS = {
  "Lista":          "kcard__tag--lista",
  "Atas":           "kcard__tag--atas",
  "Tarefas":        "kcard__tag--tarefas",
  "Diário de Obra": "kcard__tag--rdo",
  "Diário de obra": "kcard__tag--rdo",
  "Medição":        "kcard__tag--medicao",
  "Metas":          "kcard__tag--metas",
};

// Ícone material por tipo
const TAG_ICON = {
  "Lista":          "list",
  "Atas":           "description",
  "Tarefas":        "task_alt",
  "Diário de Obra": "construction",
  "Diário de obra": "construction",
  "Medição":        "straighten",
  "Metas":          "flag",
};

// ── Mapa de cores das prioridades ────────────────────────────────────────
const PRIORIDADE_CONFIG = {
  alta:  { label: "Alta",  color: "#dc2626", bg: "#fef2f2" },
  media: { label: "Média", color: "#d97706", bg: "#fffbeb" },
  baixa: { label: "Baixa", color: "#16a34a", bg: "#f0fdf4" },
};

// ── Conta todas as fotos de um RDO (gerais + por pavimento) ──────────────
const contarFotosRdo = (r) => {
  const gerais = Array.isArray(r?.fotos) ? r.fotos.length : 0;
  const porPavimento = Object.values(r?.atividades_fotos || {}).reduce(
    (acc, lista) => acc + (Array.isArray(lista) ? lista.length : 0),
    0
  );
  return gerais + porPavimento;
};

// ── Helpers de ordenação de notas ────────────────────────────────────────
const PESO_PRIORIDADE = { alta: 3, media: 2, baixa: 1 };

const DIRECAO_PADRAO = { criacao: "desc", entrega: "asc", prioridade: "desc", tags: "asc" };

const OPCOES_ORDENACAO = [
  { campo: "criacao", label: "Data de criação", icon: "schedule" },
  { campo: "entrega", label: "Data de entrega", icon: "event" },
  { campo: "prioridade", label: "Prioridade", icon: "flag" },
  { campo: "tags", label: "Tags", icon: "sell" },
];

const nomePrimeiraTag = (nota) => {
  const t = Array.isArray(nota.tags) ? nota.tags[0] : null;
  if (!t) return null;
  const nome = typeof t === "string" ? t : (t.nome || t.name || "");
  return nome ? nome.toLowerCase() : null;
};

const valorOrdenacao = (nota, campo) => {
  if (campo === "criacao") return nota.created_at ? new Date(nota.created_at).getTime() : null;
  if (campo === "entrega") return nota.data_entrega ? new Date(nota.data_entrega).getTime() : null;
  if (campo === "prioridade") return PESO_PRIORIDADE[nota.prioridade] || null;
  if (campo === "tags") return nomePrimeiraTag(nota);
  return null;
};

const ordenarNotas = (notas, campo, dir) => {
  const mult = dir === "asc" ? 1 : -1;
  return [...notas].sort((a, b) => {
    const va = valorOrdenacao(a, campo);
    const vb = valorOrdenacao(b, campo);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    if (typeof va === "string") return va.localeCompare(vb) * mult;
    return (va - vb) * mult;
  });
};

export default function Column({
  col,
  index,
  columns,
  columnsNormais,
  columnsArquivadas,
  notasConcluidas,
  notaProgresso,
  dataConclusaoEdit,
  dataConclusaoSalva,
  dataEntregaEdit,
  dataEntregaSalva,
  showColorPicker,
  menuOpenPilha,
  menuOpenNota,
  editingColumnId,
  columnTitleDraft,
  setMenuOpenPilha,
  setMenuOpenNota,
  setActiveColumnId,
  setEditingColumnId,
  setColumnTitleDraft,
  setShowColorPicker,
  setColumns,
  toggleConclusaoNota,
  setDataConclusaoEdit,
  saveDataConclusao,
  setDataEntregaEdit,
  saveDataEntrega,
  handleOpenNota,
  handleEditNota,
  handleDeleteNota,
  onSaveResponsavelRapida,
  onSaveDataEntregaRapida,
  onRemoveResponsavelRapida,
  onSavePrioridade,
  modoArquivadas,
  donoContainerId,
  usuarioId,
  entityType,
  entity,
  membros,
  expandedColumnId,
  setExpandedColumnId,
  expandedNotaView,
  setExpandedNotaView,
  renderNotaContent,
  onToggleFlutuante,
  onOpenTask,
  onGerenciarCompartilhamento,
  onAddTagNota,
  onRemoveTagNota,
}) {
  const colorTrackRefs = useRef({});
  const columnRef = useRef(null);

  const [notasRecebidosInfo, setNotasRecebidosInfo] = useState({});
  const [showModalResponsavel, setShowModalResponsavel] = useState(false);
  const [responsavelInfo, setResponsavelInfo] = useState(null);
  const [notaParaEnviar, setNotaParaEnviar] = useState(null);

  const [dragOverNotaId, setDragOverNotaId] = useState(null);
  const [notasMeta, setNotasMeta] = useState({});
  const [notasObjetivosCount, setNotasObjetivosCount] = useState({});
  const [compromissosPorNota, setCompromissosPorNota] = useState({});
  const [rdosInfoMap, setRdosInfoMap] = useState({});
  const [carregandoRdoInfo, setCarregandoRdoInfo] = useState(true);
  const [rdoInfoRefresh, setRdoInfoRefresh] = useState(0);
  const [mesVisualizadoRdo, setMesVisualizadoRdo] = useState(() => {
    const hoje = new Date();
    return { ano: hoje.getFullYear(), mes: hoje.getMonth() + 1 };
  });

  // ── Estados do menu de ordenação ──────────────────────────────────────
    const [menuOrdenarAberto, setMenuOrdenarAberto] = useState(false);
    const [ordenacaoAtiva, setOrdenacaoAtiva] = useState(
      col.ordenacao_campo ? { campo: col.ordenacao_campo, dir: col.ordenacao_dir || "asc" } : null
    );
    const menuOrdenarRef = useRef(null);

  const handleMesChangeRdo = useCallback((ano, mes) => {
    setMesVisualizadoRdo(prev => (prev.ano === ano && prev.mes === mes ? prev : { ano, mes }));
  }, []);

  // ── Ajuste: marca se a pessoa foi removida do container ──────────────
  useEffect(() => {
    if (!col.responsavel_id) { setResponsavelInfo(null); return; }
    const local = membros?.find(m => m.id === col.responsavel_id);
    if (local) { setResponsavelInfo({ ...local, removido: false }); return; }
    supabase.from("profiles").select("id, nickname, avatar_url")
      .eq("id", col.responsavel_id).single()
      .then(({ data }) => { if (data) setResponsavelInfo({ ...data, removido: true }); });
  }, [col.responsavel_id, membros]);

  // ── Sincronização em tempo real de pilha compartilhada ────────────────
  useEffect(() => {
    if (!col.compartilhada) return;
    const sub = supabase
      .channel(`pilha-compartilhada-${col.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "notas", filter: `pilha_id=eq.${col.id}` },
        async () => {
          const { data: notasRaw } = await supabase.from("notas").select("*").eq("pilha_id", col.id).order("ordem", { ascending: true });
          if (notasRaw) setColumns(prev => prev.map(c => c.id === col.id ? { ...c, notas: notasRaw } : c));
        })
      .subscribe();
    return () => { supabase.removeChannel(sub); };
  }, [col.id, col.compartilhada, setColumns]);

  useEffect(() => {
    if (!col.notas.length) return;
    const ids = col.notas.map(n => n.id);

    const carregarMeta = async () => {
      try {
        const { data: comentarios } = await supabase
          .from("comentarios")
          .select("nota_id")
          .in("nota_id", ids);

        const { data: anexos } = await supabase
          .from("anexos")
          .select("nota_id, mime_type")
          .in("nota_id", ids);

        const meta = {};
        ids.forEach(id => {
          const totalComentarios = comentarios?.filter(c => c.nota_id === id).length || 0;
          const totalAnexos = anexos?.filter(a => a.nota_id === id && !a.mime_type?.startsWith("image/")).length || 0;
          const totalImagens = anexos?.filter(a => a.nota_id === id && a.mime_type?.startsWith("image/")).length || 0;
          if (totalComentarios || totalAnexos || totalImagens) {
            meta[id] = { comentarios: totalComentarios, anexos: totalAnexos, imagens: totalImagens };
          }
        });
        setNotasMeta(meta);
      } catch (err) {
        console.error("Erro ao carregar meta dos cards:", err);
      }
    };

    carregarMeta();
  }, [col.notas]);

  useEffect(() => {
    const tarefasIds = col.notas.filter(n => n.tipo === "Tarefas").map(n => n.id);
    if (!tarefasIds.length) { setNotasObjetivosCount({}); return; }

    const carregarObjetivosTarefas = async () => {
      try {
        const { data: checklists } = await supabase
          .from("checklists")
          .select("id, nota_id")
          .in("nota_id", tarefasIds);
        if (!checklists?.length) { setNotasObjetivosCount({}); return; }

        const checklistIds = checklists.map(c => c.id);
        const { data: items } = await supabase
          .from("checklist_items")
          .select("checklist_id, is_completed")
          .in("checklist_id", checklistIds);

        const counts = {};
        checklists.forEach(c => {
          const itens = items?.filter(i => i.checklist_id === c.id) || [];
          if (itens.length) {
            counts[c.nota_id] = { total: itens.length, concluidos: itens.filter(i => i.is_completed).length };
          }
        });
        setNotasObjetivosCount(counts);
      } catch (err) {
        console.error("Erro ao carregar objetivos das tarefas:", err);
      }
    };

    carregarObjetivosTarefas();
  }, [col.notas]);

  useEffect(() => {
    const tarefasIds = col.notas.filter(n => n.tipo === "Tarefas").map(n => n.id);
    if (!tarefasIds.length) { setCompromissosPorNota({}); return; }

    const carregarCompromissos = async () => {
      try {
        const { data: comps } = await supabase
          .from("compromissos")
          .select("id, nota_id, titulo, data_compromisso, hora_inicio, concluido")
          .in("nota_id", tarefasIds)
          .order("data_compromisso", { ascending: true });

        const mapa = (comps || []).reduce((acc, c) => {
          if (!acc[c.nota_id]) acc[c.nota_id] = [];
          acc[c.nota_id].push(c);
          return acc;
        }, {});
        setCompromissosPorNota(mapa);
      } catch (err) {
        console.error("Erro ao carregar compromissos dos cards:", err);
      }
    };

    carregarCompromissos();
  }, [col.notas]);

  // ── Info dos RDOs (responsável + total de fotos) ─────────────────────
  useEffect(() => {
    const notasRdo = col.notas.filter(n => n.tipo === "Diário de Obra");
    if (!notasRdo.length) {
      setRdosInfoMap({});
      setCarregandoRdoInfo(false);
      return;
    }
    const ids = notasRdo.map(n => n.id);

    const carregarRdosInfo = async () => {
      try {
        const { data: rdosRaw } = await supabase
          .from("rdos")
          .select("nota_id, responsavel_preenchimento, fotos, atividades_fotos")
          .in("nota_id", ids);

        const mapa = {};
        ids.forEach(id => {
          const r = rdosRaw?.find(x => String(x.nota_id) === String(id));
          mapa[id] = {
            temRdo: !!r,
            responsavel: r?.responsavel_preenchimento || "",
            totalFotos: r ? contarFotosRdo(r) : 0,
          };
        });
        setRdosInfoMap(mapa);
      } catch (err) {
        console.error("Erro ao carregar info dos RDOs:", err);
      } finally {
        setCarregandoRdoInfo(false);
      }
    };

    carregarRdosInfo();
  }, [col.notas, rdoInfoRefresh]);

  // ── Recarrega info dos RDOs quando um RDO for salvo ──────────────────
  useEffect(() => {
    if (col.tipo_pilha !== "diario_obras") return;
    const handler = () => setRdoInfoRefresh(n => n + 1);
    window.addEventListener("rdoSalvo", handler);
    return () => window.removeEventListener("rdoSalvo", handler);
  }, [col.tipo_pilha]);

  const handleSaveResponsavelPilha = async (novoResponsavelId) => {
    const { error } = await supabase.from("pilhas").update({ responsavel_id: novoResponsavelId }).eq("id", col.id);
    if (error) { alert("Erro ao salvar responsável."); return; }
    setColumns(prev => prev.map(c => c.id === col.id ? { ...c, responsavel_id: novoResponsavelId } : c));
  };

  const getDiaSemana = (d) => {
    if (!d) return "";
    const dias = ["Domingo","Segunda-feira","Terça-feira","Quarta-feira","Quinta-feira","Sexta-feira","Sábado"];
    return dias[new Date(d + "T00:00:00").getUTCDay()];
  };

  const getDiaSemanaAbrev = (d) => {
    if (!d) return "";
    const dias = ["DOM","SEG","TER","QUA","QUI","SEX","SÁB"];
    return dias[new Date(d + "T00:00:00").getUTCDay()];
  };

  const getMesAbrev = (d) => {
    if (!d) return "";
    const meses = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
    return meses[new Date(d + "T00:00:00").getUTCMonth()];
  };

  const getDiaNumero = (d) => {
    if (!d) return "";
    return String(new Date(d + "T00:00:00").getUTCDate()).padStart(2, "0");
  };

  const calcularDiasAtraso = (dataEntrega) => {
    if (!dataEntrega) return 0;
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    const [ano, mes, dia] = dataEntrega.split("-").map(Number);
    const dataRdo = new Date(ano, mes - 1, dia);
    return Math.floor((hoje - dataRdo) / (1000 * 60 * 60 * 24));
  };

  const formatarDataLocal = (d) => {
    if (!d) return null;
    const [a, m, dia] = d.split('-');
    return new Date(a, m - 1, dia).toLocaleDateString("pt-BR");
  };

  const formatarDataEnvio = (d) => {
    if (!d) return '';
    try {
      const dt = new Date(d);
      return `${dt.toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric' })} às ${dt.toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' })}`;
    } catch { return ''; }
  };

  const isRecebidos = col.title === "Recebidos";
  const isArquivo = modoArquivadas && !isRecebidos;
  // ── Cor default roxa quando compartilhada ────────────────────────────
  const bgColor = col.cor_fundo || (col.compartilhada ? "#f5f3ff" : "transparent");
  const isColorPickerVisible = showColorPicker[col.id];
  const isDiarioObra = col.tipo_pilha === "diario_obras";
  const isExpanded = expandedColumnId === col.id;

  const notificarResponsavelPilha = async (nota, pilhaId) => {
    const pilhaAlvo = [...(columnsNormais||[]),...(columnsArquivadas||[])].find(c => c.id === pilhaId);
    const respId = pilhaAlvo?.responsavel_id || col.responsavel_id;
    if (!respId || respId === usuarioId) return;
    try {
      const params = new URLSearchParams(window.location.search);
      params.set("nota", nota.id);
      await supabase.from("notificacoes").insert([{
        user_id: respId, remetente_id: usuarioId, tipo: "nota_adicionada_pilha",
        mensagem: `Nova nota "${nota.nome}" adicionada à pilha "${pilhaAlvo?.title || col.title}" — você é o responsável.`,
        tipo_nota: nota.tipo || null, pilha_id: pilhaId ? String(pilhaId) : null,
        nota_id: nota.id ? String(nota.id) : null,
        projeto_id: entity?.id && entityType === "project" ? entity.id : null,
        setor_id: entity?.id && entityType === "setor" ? entity.id : null,
        container_id: donoContainerId || null,
        url: `${window.location.pathname}?${params.toString()}`, lido: false,
      }]);
    } catch (err) { console.error("Erro ao criar notificação:", err); }
  };

  const handleSaveDescricaoRapida = async (notaId, descricao) => {
    const { error } = await supabase.from("notas").update({ descricao }).eq("id", notaId);
    if (error) return;
    setColumns(prev => prev.map(c =>
      c.id === col.id ? { ...c, notas: c.notas.map(n => n.id === notaId ? { ...n, descricao } : n) } : c
    ));
  };

  const handleCreateNotaRapida = async () => {
    const { data: newNota, error } = await supabase
      .from("notas")
      .insert([{
        nome: "Nova Nota Rápida",
        tipo: "Nota Rápida",
        pilha_id: col.id,
        responsavel: usuarioId,
        concluida: false,
        ordem: 0,
      }])
      .select()
      .single();

    if (error) {
      alert("Erro ao criar nota rápida: " + error.message);
      return;
    }

    setColumns(prev => prev.map(c =>
      c.id === col.id ? { ...c, notas: [newNota, ...c.notas] } : c
    ));

  };

  const updatePilhaCor = async (pilhaId, cor) => {
    const { error } = await supabase.from("pilhas").update({ cor_fundo: cor }).eq("id", pilhaId);
    if (!error) setColumns(prev => prev.map(c => c.id === pilhaId ? { ...c, cor_fundo: cor } : c));
  };

  const handleResetCor = (pilhaId) => {
    updatePilhaCor(pilhaId, null);
    setShowColorPicker(prev => ({ ...prev, [pilhaId]: false }));
  };

  const toggleColorPicker = (pilhaId, show) => setShowColorPicker(prev => ({ ...prev, [pilhaId]: show }));

  const saveColumnTitle = async (id) => {
    if (!columnTitleDraft.trim()) return setEditingColumnId(null);
    const { error } = await supabase.from("pilhas").update({ title: columnTitleDraft }).eq("id", id);
    if (!error) setColumns(prev => prev.map(c => c.id === id ? { ...c, title: columnTitleDraft } : c));
    setEditingColumnId(null);
  };

  const handleDeletePilha = async (pilhaId) => {
    const pilha = columns.find(c => c.id === pilhaId);
    if (!pilha || pilha.notas.length > 0) { alert("Apenas pilhas vazias podem ser excluídas."); return; }
    if (!window.confirm(`Excluir a pilha "${pilha.title}"?`)) return;
    const { error } = await supabase.from("pilhas").delete().eq("id", pilhaId);
    if (!error) { setColumns(prev => prev.filter(c => c.id !== pilhaId)); setMenuOpenPilha(null); }
  };

  const handleArquivarNota = async (nota, pilhaAtualId) => {
    const estaEmArquivo = col.arquivada;
    const pilhasAlvo = columns.filter(c => c.arquivada !== estaEmArquivo);
    if (pilhasAlvo.length === 0) { alert(estaEmArquivo ? "Sem pilhas normais." : "Sem pilhas de arquivo."); return; }
    const pilhaAlvoId = pilhasAlvo[0].id;
    try {
      const updates = { pilha_id: pilhaAlvoId, ordem: 0 };
      if (!estaEmArquivo) updates.pilha_original_id = pilhaAtualId;
      const { error } = await supabase.from("notas").update(updates).eq("id", nota.id);
      if (error) throw error;
      setColumns(prev => prev.map(c => {
        if (c.id === pilhaAtualId) return { ...c, notas: c.notas.filter(n => n.id !== nota.id) };
        if (c.id === pilhaAlvoId) return { ...c, notas: [{ ...nota, ...updates }, ...c.notas] };
        return c;
      }));
    } catch (err) { console.error("Erro ao mover nota:", err); alert("Erro ao mover a nota."); }
  };

  const handleToggleExpand = (e) => {
    e.stopPropagation();
    if (isExpanded) { setExpandedColumnId(null); setExpandedNotaView(null); }
    else { setExpandedColumnId(col.id); setExpandedNotaView(null); }
  };

  // ── Fecha menu de ordenação ao clicar fora ────────────────────────────
  useEffect(() => {
    if (!menuOrdenarAberto) return;
    const fn = (e) => {
      if (menuOrdenarRef.current && !menuOrdenarRef.current.contains(e.target)) setMenuOrdenarAberto(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [menuOrdenarAberto]);

  // ── Aplica ordenação na pilha e persiste a nova ordem no banco ────────
    const handleOrdenarNotas = async (campo) => {
      const dir = ordenacaoAtiva?.campo === campo
        ? (ordenacaoAtiva.dir === "asc" ? "desc" : "asc")
        : DIRECAO_PADRAO[campo];
      const ordenadas = ordenarNotas(col.notas, campo, dir).map((n, idx) => ({ ...n, ordem: idx }));
      setColumns(prev => prev.map(c => c.id === col.id
        ? { ...c, notas: ordenadas, ordenacao_campo: campo, ordenacao_dir: dir }
        : c));
      setOrdenacaoAtiva({ campo, dir });
      setMenuOrdenarAberto(false);
      try {
        const resultados = await Promise.all([
          supabase.from("pilhas").update({ ordenacao_campo: campo, ordenacao_dir: dir }).eq("id", col.id),
          ...ordenadas.map(n => supabase.from("notas").update({ ordem: n.ordem }).eq("id", n.id)),
        ]);
        if (resultados.some(r => r.error)) alert("Erro ao salvar ordenação.");
      } catch {
        alert("Erro ao salvar ordenação.");
      }
    };

  const temPrioridadeNaPilha = col.notas.some(n => n.prioridade);
  const temTagsNaPilha = col.notas.some(n => Array.isArray(n.tags) && n.tags.length > 0);
  const opcoesOrdenacaoVisiveis = OPCOES_ORDENACAO.filter(o =>
    (o.campo !== "prioridade" || temPrioridadeNaPilha) && (o.campo !== "tags" || temTagsNaPilha)
  );

  useEffect(() => {
    if (!col.notas.length) return;
    const notasComOrigem = col.notas.filter(n => n.projeto_origem_id);
    if (!notasComOrigem.length) return;
    const carregar = async () => {
      const infos = {};
      for (const nota of notasComOrigem) {
        if (!nota.projeto_origem_id) continue;
        try {
          const { data: projeto } = await supabase.from('projects').select('name').eq('id', nota.projeto_origem_id).single();
          let enviadoPorNome = nota.enviado_por_nome || 'Usuário';
          if (nota.enviado_por_id) {
            const { data: perfil } = await supabase.from('profiles').select('nickname, nome').eq('id', nota.enviado_por_id).single();
            if (perfil) enviadoPorNome = perfil.nickname || perfil.nome || 'Usuário';
          }
          infos[nota.id] = { projetoNome: projeto?.name || 'Projeto desconhecido', enviadoPor: enviadoPorNome, dataEnvio: nota.data_envio };
        } catch {}
      }
      setNotasRecebidosInfo(infos);
    };
    carregar();
  }, [col.notas]);

  useEffect(() => {
    if (!showColorPicker[col.id]) return;
    const track = colorTrackRefs.current[col.id];
    if (!track) return;
    const handleClick = (e) => {
      const rect = track.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      updatePilhaCor(col.id, `hsl(${Math.round(pct * 360)}, 40%, 94%)`);
    };
    track.addEventListener("click", handleClick);
    return () => track.removeEventListener("click", handleClick);
  }, [showColorPicker, col.id]);

  useEffect(() => {
    if (!isDiarioObra) return;
    const handleNovaNotaRDO = ({ detail: { nota, pilhaId: evPilhaId } }) => {
      if (evPilhaId === col.id) setColumns(prev => prev.map(c => c.id === col.id ? { ...c, notas: [nota, ...c.notas] } : c));
    };
    const handleRdoAtualizado = ({ detail: { notaId, data: upd } }) => {
      setColumns(prev => prev.map(c => c.id === col.id ? { ...c, notas: c.notas.map(n => n.id === notaId ? { ...n, ...upd } : n) } : c));
    };
    window.addEventListener('novaNotaRDO', handleNovaNotaRDO);
    window.addEventListener('rdoAtualizado', handleRdoAtualizado);
    const sub = supabase.channel(`rdo-${col.id}`)
      .on('postgres_changes', { event:'*', schema:'public', table:'notas', filter:`pilha_id=eq.${col.id}` }, async (payload) => {
        if (payload.new?.tipo === "Diário de Obra" || payload.old?.tipo === "Diário de Obra") {
          const { data: notasRaw } = await supabase.from("notas").select("*").eq("pilha_id", col.id).eq("tipo","Diário de Obra").order("data_entrega",{ ascending:false });
          if (notasRaw) setColumns(prev => prev.map(c => c.id === col.id ? { ...c, notas: notasRaw } : c));
        }
      }).subscribe();
    return () => {
      window.removeEventListener('novaNotaRDO', handleNovaNotaRDO);
      window.removeEventListener('rdoAtualizado', handleRdoAtualizado);
      supabase.removeChannel(sub);
    };
  }, [col.id, isDiarioObra, setColumns]);

  const openNotaById = async (notaId) => {
    for (const c of [...columnsNormais, ...columnsArquivadas]) {
      const nota = c.notas.find(n => n.id === notaId);
      if (nota) { handleOpenNota(nota); return; }
    }
    const { data: nota, error } = await supabase.from("notas").select("*").eq("id", notaId).single();
    if (error || !nota) { alert("Nota não encontrada."); return; }
    handleOpenNota(nota);
  };

  const handleDragOver = (e, notaId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setDragOverNotaId(notaId);
  };

  const handleDragLeave = () => setDragOverNotaId(null);

  const handleDrop = async (e, notaId) => {
    e.preventDefault();
    setDragOverNotaId(null);
    const tagId = e.dataTransfer.getData("tag");
    if (tagId) { await onAddTagNota?.(notaId, tagId); return; }
    const prioridade = e.dataTransfer.getData("prioridade");
    if (!prioridade) return;
    await onSavePrioridade?.(notaId, prioridade);
  };

  const handleNotaEnviadaMesmoContainer = ({ tipoOperacao, notaId }) => {
    if (tipoOperacao === "mover") {
      setColumns(prev => prev.map(c => c.id === col.id ? { ...c, notas: c.notas.filter(n => n.id !== notaId) } : c));
    }
  };

  if (isExpanded) {
    return (
      <CardPilhaExpand
        col={col} isArquivo={isArquivo} notasConcluidas={notasConcluidas}
        notaProgresso={notaProgresso} dataConclusaoEdit={dataConclusaoEdit}
        dataConclusaoSalva={dataConclusaoSalva} dataEntregaEdit={dataEntregaEdit}
        dataEntregaSalva={dataEntregaSalva} menuOpenNota={menuOpenNota}
        setMenuOpenNota={setMenuOpenNota} handleOpenNota={handleOpenNota}
        handleEditNota={handleEditNota} handleDeleteNota={handleDeleteNota}
        toggleConclusaoNota={toggleConclusaoNota} onSaveResponsavelRapida={onSaveResponsavelRapida}
        onSaveDataEntregaRapida={onSaveDataEntregaRapida} onRemoveResponsavelRapida={onRemoveResponsavelRapida}
        handleSaveDescricaoRapida={handleSaveDescricaoRapida} setDataConclusaoEdit={setDataConclusaoEdit}
        saveDataConclusao={saveDataConclusao} setDataEntregaEdit={setDataEntregaEdit}
        saveDataEntrega={saveDataEntrega} donoContainerId={donoContainerId}
        usuarioId={usuarioId} entityType={entityType} entity={entity} membros={membros}
        expandedNotaView={expandedNotaView} setExpandedNotaView={setExpandedNotaView}
        setExpandedColumnId={setExpandedColumnId} handleArquivarNota={handleArquivarNota}
        renderNotaContent={renderNotaContent}
      />
    );
  }

  const getTagClass = (tipo) => TAG_CLASS[tipo] || "kcard__tag--default";
  const getTagIcon = (tipo) => TAG_ICON[tipo] || "sticky_note_2";

  const renderCard = (nota, idx) => {
    if (nota.tipo === "Nota Rápida") {
      const isConcluida = notasConcluidas.has(String(nota.id));
      const isEditingDate = dataConclusaoEdit.hasOwnProperty(String(nota.id));
      return (
        <Draggable key={String(nota.id)} draggableId={String(nota.id)} index={idx} type="CARD">
          {(prov) => (
            <div ref={prov.innerRef} {...prov.draggableProps} style={{ ...prov.draggableProps.style, userSelect:"text" }}>
              <NotaRapidaCard nota={nota} onSaveResponsavel={onSaveResponsavelRapida}
                onSaveDataEntrega={onSaveDataEntregaRapida} onSaveDescricao={handleSaveDescricaoRapida}
                onRemoveResponsavel={onRemoveResponsavelRapida} isConcluida={isConcluida}
                isEditingDate={isEditingDate} dataConclusaoEdit={dataConclusaoEdit}
                dataConclusaoSalva={dataConclusaoSalva} setDataConclusaoEdit={setDataConclusaoEdit}
                saveDataConclusao={saveDataConclusao} menuOpenNota={menuOpenNota}
                setMenuOpenNota={setMenuOpenNota} handleEditNota={handleEditNota}
                handleDeleteNota={handleDeleteNota} toggleConclusaoNota={toggleConclusaoNota}
                pilhaId={col.id} dragHandleProps={prov.dragHandleProps}
                containerId={donoContainerId} usuarioId={usuarioId}
                entityType={entityType} entityId={entity?.id}
                onToggleFlutuante={() => onToggleFlutuante(nota, col.id)}
                onOpenTask={onOpenTask}
                onSavePrioridade={onSavePrioridade}
                onEnviarNota={podeEnviarNota(nota) ? () => setNotaParaEnviar(nota) : undefined}
                notasDisponiveis={columns.flatMap(c => c.notas).filter(n2 => n2.id !== nota.id && n2.tipo !== "Nota Rápida")} />
            </div>
          )}
        </Draggable>
      );
    }

    const isConcluida = notasConcluidas.has(String(nota.id));
    const usarDataEntrega = nota.tipo === "Tarefas";
    const isEditingDate = usarDataEntrega
      ? dataEntregaEdit?.hasOwnProperty(String(nota.id))
      : dataConclusaoEdit?.hasOwnProperty(String(nota.id));
    const dataAtual = usarDataEntrega ? dataEntregaSalva : dataConclusaoSalva;
    const dataEdit  = usarDataEntrega ? dataEntregaEdit  : dataConclusaoEdit;
    const setDataEdit = usarDataEntrega ? setDataEntregaEdit : setDataConclusaoEdit;
    const saveData  = usarDataEntrega ? saveDataEntrega  : saveDataConclusao;

    const infoRecebido = notasRecebidosInfo[nota.id];
    const progresso = notaProgresso[nota.id];
    const tagClass = getTagClass(nota.tipo);
    const tagIcon  = getTagIcon(nota.tipo);

    let cardStyle = {};
    if (nota.respondida)    cardStyle = { borderLeft: "3px solid #22c55e" };
    else if (nota.enviada)  cardStyle = { borderLeft: "3px solid #ef4444" };

    const prioInfo = nota.prioridade ? PRIORIDADE_CONFIG[nota.prioridade] : null;
    const objInfo = nota.tipo === "Tarefas" ? notasObjetivosCount[nota.id] : null;
    const compsNota = nota.tipo === "Tarefas" ? (compromissosPorNota[nota.id] || []) : [];
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);

    return (
      <Draggable key={String(nota.id)} draggableId={String(nota.id)} index={idx} type="CARD">
        {(prov, snap) => (
          <div
            className={`kcard${snap.isDragging ? " dragging" : ""}${isConcluida ? " concluida" : ""}${dragOverNotaId === nota.id ? " kcard--drop-over" : ""}`}
            ref={prov.innerRef}
            {...prov.draggableProps}
            {...prov.dragHandleProps}
            style={{ ...prov.draggableProps.style, ...cardStyle }}
            onClick={() => handleOpenNota(nota)}
            onDragOver={e => handleDragOver(e, nota.id)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, nota.id)}
          >
            <div className="kcard__check-wrap" onClick={e => e.stopPropagation()}>
              <input type="checkbox" checked={isConcluida} readOnly className="kcard__check"
                onClick={e => { e.stopPropagation(); toggleConclusaoNota(nota.id, isConcluida); }} />
              {isConcluida && (
                <button className="kcard__archive-btn" title={col.arquivada ? "Restaurar" : "Arquivar"}
                  onClick={e => { e.stopPropagation(); handleArquivarNota(nota, col.id); }}>
                  <FaFileExport size={11} />
                </button>
              )}
            </div>

            <div className="kcard__tag-row">
              <span className={`kcard__tag ${tagClass}`}>
                <span className="material-symbols-outlined" style={{ fontSize:12 }}>{tagIcon}</span>
                {nota.tipo}
              </span>

              {prioInfo && (
                <span className="kcard__priority-badge"
                  style={{ "--pri-color": prioInfo.color, "--pri-bg": prioInfo.bg }}
                  title={`${prioInfo.label} Prioridade — clique para remover`}
                  onClick={async e => {
                    e.stopPropagation();
                    await onSavePrioridade?.(nota.id, null);
                  }}
                >
                  <FaFlag size={9} style={{ color: prioInfo.color }} />
                  <span>{prioInfo.label}</span>
                </span>
              )}

              {!isConcluida && (
                <div onClick={e => e.stopPropagation()} style={{ position:"relative" }}>
                  <button className="kcard__menu-btn"
                    onClick={() => setMenuOpenNota(menuOpenNota === nota.id ? null : nota.id)}>
                    <span className="material-symbols-outlined" style={{ fontSize:16 }}>more_horiz</span>
                  </button>
                  {menuOpenNota === nota.id && (
                    <div className="kcard__dropdown">
                      <button onClick={() => handleEditNota(nota, col.id)}><FaEdit size={12}/> Editar</button>
                      <button onClick={() => handleDeleteNota(nota.id, col.id)}><FaTrash size={12}/> Excluir</button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <p className="kcard__title">{nota.nome}</p>

            {compsNota.length > 0 && (
              <div className="kcard__compromissos">
                {compsNota.map(c => {
                  const dataComp = new Date(c.data_compromisso + "T00:00:00");
                  const atrasado = !c.concluido && dataComp < hoje;
                  const statusCls = c.concluido
                    ? "kcard__comp-badge--concluido"
                    : atrasado
                    ? "kcard__comp-badge--atrasado"
                    : "kcard__comp-badge--futuro";
                  const horaStr  = c.hora_inicio ? c.hora_inicio.slice(0, 5) : null;
                  const diaStr   = dataComp.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
                  return (
                    <span key={c.id} className={`kcard__comp-badge ${statusCls}`} title={c.titulo}>
                      <span className="material-symbols-outlined" style={{ fontSize: 11 }}>event</span>
                      <span className="kcard__comp-badge-titulo">{c.titulo}</span>
                      <span className="kcard__comp-badge-data">{diaStr}{horaStr ? ` · ${horaStr}` : ""}</span>
                    </span>
                  );
                })}
              </div>
            )}

            {((nota.tipo === "Atas" && progresso !== undefined) || (objInfo?.total > 0)) && (
              <div className="kcard__progress-wrap">
                <div className="kcard__progress-bar">
                  <div
                    className="kcard__progress-fill"
                    style={{ width: `${nota.tipo === "Atas" ? progresso : Math.round((objInfo.concluidos / objInfo.total) * 100)}%` }}
                  />
                </div>
                <span className="kcard__progress-pct">
                  {nota.tipo === "Atas" ? `${progresso}%` : `${objInfo.concluidos}/${objInfo.total}`}
                </span>
              </div>
            )}

            {nota.projeto_origem_id && infoRecebido ? (
              <div className="kcard__received-info">
                {infoRecebido.projetoNome && <span>{infoRecebido.projetoNome}</span>}
                {infoRecebido.enviadoPor  && <span>{infoRecebido.enviadoPor}</span>}
                {infoRecebido.dataEnvio   && <span>{formatarDataEnvio(infoRecebido.dataEnvio)}</span>}
              </div>
            ) : (
              <div className="kcard__bottom-row" onClick={e => e.stopPropagation()}>
                <div className="kcard__badges">
                  {podeEnviarNota(nota) && (
                    <button
                      className="kcard__send-btn"
                      title="Enviar para outro container"
                      onClick={e => { e.stopPropagation(); setNotaParaEnviar(nota); }}
                    >
                      <span className="material-symbols-outlined">ios_share</span>
                    </button>
                  )}
                  {nota.enviada || nota.respondida ? (
                    <>
                      <span className="kcard__badge">
                        <span className="material-symbols-outlined">calendar_today</span>
                        {nota.data_envio ? formatarDataEnvio(nota.data_envio) : "—"}
                      </span>
                      {nota.enviado_por_nome && (
                        <span className="kcard__badge kcard__badge--sender">
                          <span className="material-symbols-outlined">person</span>
                          {nota.enviado_por_nome}
                        </span>
                      )}
                    </>
                  ) : isEditingDate ? (
                    <div className="kcard__date-input-row">
                      <span className="material-symbols-outlined" style={{ fontSize:13, color:"#9ca3af" }}>calendar_today</span>
                      <input type="date" value={dataEdit?.[nota.id] || ""}
                        onChange={e => setDataEdit(prev => ({ ...prev, [nota.id]: e.target.value }))} />
                      <button style={{ color:"#22c55e" }}
                        onClick={() => saveData(nota.id, dataEdit[nota.id])}>✓</button>
                      <button style={{ color:"#ef4444" }}
                        onClick={() => setDataEdit(prev => { const cp={...prev}; delete cp[nota.id]; return cp; })}>✖</button>
                    </div>
                  ) : (
                    <span className="kcard__badge"
                      onClick={() => setDataEdit(prev => ({ ...prev, [nota.id]: dataAtual?.[nota.id] || "" }))}>
                      <span className="material-symbols-outlined">calendar_today</span>
                      {dataAtual?.[nota.id] ? formatarDataLocal(dataAtual[nota.id]) : (usarDataEntrega ? "Entrega" : "Data")}
                    </span>
                  )}
                </div>

                {nota.respondida ? (
                  <span className="kcard__send-status kcard__send-status--respondida" title="Respondida">
                    <span className="material-symbols-outlined">mark_email_read</span>
                  </span>
                ) : nota.enviada ? (
                  <span className="kcard__send-status kcard__send-status--enviada" title="Enviada">
                    <span className="material-symbols-outlined">forward_to_inbox</span>
                  </span>
                ) : (notasMeta[nota.id] || objInfo?.total > 0) ? (() => {
                  const m = notasMeta[nota.id] || {};
                  return (
                    <div className="kcard__meta">
                      {objInfo?.total > 0 && (
                        <span className="kcard__meta-item" title={`${objInfo.concluidos} de ${objInfo.total} objetivos concluídos`}>
                          <span className="material-symbols-outlined">checklist</span>
                          {objInfo.concluidos}/{objInfo.total}
                        </span>
                      )}
                      {m.comentarios > 0 && (
                        <span className="kcard__meta-item" title={`${m.comentarios} comentário(s)`}>
                          <span className="material-symbols-outlined">chat_bubble</span>
                          {m.comentarios}
                        </span>
                      )}
                      {m.anexos > 0 && (
                        <span className="kcard__meta-item" title={`${m.anexos} anexo(s)`}>
                          <span className="material-symbols-outlined">attach_file</span>
                          {m.anexos}
                        </span>
                      )}
                      {m.imagens > 0 && (
                        <span className="kcard__meta-item" title={`${m.imagens} imagem(ns)`}>
                          <span className="material-symbols-outlined">image</span>
                          {m.imagens}
                        </span>
                      )}
                    </div>
                  );
                })() : null}
              </div>
            )}

            {Array.isArray(nota.tags) && nota.tags.length > 0 && (
              <div className="kcard__tags" onClick={e => e.stopPropagation()}>
                {nota.tags.map(t => (
                  <span
                    key={t.id}
                    className="kcard__tag-chip"
                    style={{
                      "--tag-color": t.cor,
                      fontWeight: t.negrito ? 700 : 500,
                      fontStyle: t.italico ? "italic" : "normal",
                    }}
                    title={t.nome}
                  >
                    <span className="kcard__tag-text">{t.nome}</span>
                    <button
                      type="button"
                      className="kcard__tag-x"
                      title="Remover tag"
                      onClick={() => onRemoveTagNota?.(nota.id, t.id)}
                    >×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </Draggable>
    );
  };

  return (
    <>
      {showModalResponsavel && (
        <ModalResponsavelPilha pilha={col} membros={membros||[]} usuarioId={usuarioId}
          onSave={handleSaveResponsavelPilha} onClose={() => setShowModalResponsavel(false)} />
      )}

      {notaParaEnviar && (
        <EnviarNotaModal
          nota={notaParaEnviar}
          containerOrigemId={donoContainerId}
          usuarioId={usuarioId}
          entityAtualTipo={entityType}
          entityAtualId={entity?.id}
          onNotaEnviada={handleNotaEnviadaMesmoContainer}
          onClose={() => setNotaParaEnviar(null)}
        />
      )}

      <Draggable key={col.id} draggableId={col.id} index={index} type="COLUMN"
        isDragDisabled={isRecebidos || !!expandedColumnId}>
        {(colProvided, colSnapshot) => (
          <div
            ref={colProvided.innerRef}
            {...colProvided.draggableProps}
            style={{
              ...colProvided.draggableProps.style,
              opacity: colSnapshot.isDragging ? 0.85 : 1,
              display: expandedColumnId && expandedColumnId !== col.id ? 'none' : 'block',
            }}
          >
            <div
              className={`kanban-column${isDiarioObra ? " kanban-column--rdo" : ""}${isArquivo ? " kanban-column--arquivo" : ""}${col.compartilhada ? " kanban-column--compartilhada" : ""}`}
              style={{ background: bgColor !== "transparent" ? bgColor : undefined }}
            >
              {isColorPickerVisible && !isRecebidos && (
                <div className="color-picker-toolbar">
                  <button className="reset-color-dot" onClick={e => { e.stopPropagation(); handleResetCor(col.id); }} title="Cor original" />
                  <div ref={el => (colorTrackRefs.current[col.id] = el)} className="color-track" />
                  <button className="close-color-picker" onClick={() => toggleColorPicker(col.id, false)}><FaTimes size={11}/></button>
                </div>
              )}

              <div className="kanban-column__header" ref={columnRef}>
                {!isRecebidos && (
                  <div {...colProvided.dragHandleProps} className="kanban-column__drag-handle">
                    ⠿
                  </div>
                )}

                <div className={`kanban-column__status-dot ${isRecebidos ? "status-dot--green" : isArquivo ? "status-dot--gray" : "status-dot--blue"}`}>
                  <span className="material-symbols-outlined" style={{ fontSize:12 }}>
                    {isRecebidos ? "inbox" : isArquivo ? "folder" : isDiarioObra ? "construction" : "view_column"}
                  </span>
                </div>

                {editingColumnId === col.id && !isRecebidos ? (
                  <input className="kanban-column__title-input" type="text" value={columnTitleDraft} autoFocus
                    onChange={e => setColumnTitleDraft(e.target.value)}
                    onBlur={() => saveColumnTitle(col.id)}
                    onKeyDown={e => e.key === "Enter" && saveColumnTitle(col.id)} />
                ) : (
                  <span className="kanban-column__title"
                    onDoubleClick={() => { if (!isRecebidos) { setEditingColumnId(col.id); setColumnTitleDraft(col.title); } }}>
                    {modoArquivadas && !isRecebidos ? "Arquivo" : col.title}
                  </span>
                )}

                {col.compartilhada && (
                  <button
                    type="button"
                    className="kanban-column__compartilhada-badge"
                    style={{ cursor: "pointer", border: "none" }}
                    title={`Compartilhada com: ${(col.entidadesCompartilhadas || []).map(e => e.nome).join(", ") || "—"} — clique para gerenciar`}
                    onClick={e => { e.stopPropagation(); onGerenciarCompartilhamento?.(col); }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 13 }}>share</span>
                  </button>
                )}

                <span className="kanban-column__count">{col.notas.length}</span>
              </div>

              {!isRecebidos && !isArquivo && (
                <div className="kanban-column__actions">
                  {!isDiarioObra && (
                    <button className="kanban-col-btn" title="Nova nota" onClick={() => setActiveColumnId(col.id)}>
                      <span className="material-symbols-outlined">add</span>
                    </button>
                  )}
                  {!isDiarioObra && (
                    <button className="kanban-col-btn" title="Nova nota rápida" onClick={handleCreateNotaRapida}>
                      <span className="material-symbols-outlined">sticky_note_2</span>
                    </button>
                  )}
                  <button className="kanban-col-btn" title="Cor" onClick={e => { e.stopPropagation(); toggleColorPicker(col.id, true); }}>
                    <span className="material-symbols-outlined">palette</span>
                  </button>

                  {!isDiarioObra && (
                    <div className="kanban-col-sort" ref={menuOrdenarRef}>
                      <button
                        className={`kanban-col-btn${ordenacaoAtiva ? " kanban-col-btn--active" : ""}`}
                        title="Ordenar notas"
                        onClick={e => { e.stopPropagation(); setMenuOrdenarAberto(v => !v); }}
                      >
                        <span className="material-symbols-outlined">sort</span>
                      </button>
                      {menuOrdenarAberto && (
                        <div className="kanban-col-sort__menu" onClick={e => e.stopPropagation()}>
                          <div className="kanban-col-sort__titulo">Ordenar por</div>
                          {opcoesOrdenacaoVisiveis.map(o => {
                            const ativa = ordenacaoAtiva?.campo === o.campo;
                            return (
                              <button
                                key={o.campo}
                                className={`kanban-col-sort__item${ativa ? " kanban-col-sort__item--ativo" : ""}`}
                                onClick={() => handleOrdenarNotas(o.campo)}
                              >
                                <span className="material-symbols-outlined">{o.icon}</span>
                                <span>{o.label}</span>
                                {ativa && (
                                  <span className="material-symbols-outlined kanban-col-sort__dir">
                                    {ordenacaoAtiva.dir === "asc" ? "arrow_upward" : "arrow_downward"}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  <button className={`kanban-col-btn${responsavelInfo ? " kanban-col-btn--active" : ""}`}
                    title={responsavelInfo ? `Responsável: ${responsavelInfo.nickname}` : "Designar responsável"}
                    onClick={e => { e.stopPropagation(); setShowModalResponsavel(true); }}>
                    <span className="material-symbols-outlined">person_pin_circle</span>
                  </button>
                  <button className="kanban-col-btn" title="Expandir" onClick={handleToggleExpand}>
                    <span className="material-symbols-outlined">expand_content</span>
                  </button>
                  <button className="kanban-col-btn kanban-col-btn--danger"
                    title={col.notas.length > 0 ? "Pilha não vazia" : "Excluir"}
                    disabled={col.notas.length > 0}
                    onClick={async e => { e.stopPropagation(); if (col.notas.length === 0) await handleDeletePilha(col.id); }}>
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                </div>
              )}

              {col.compartilhada && col.entidadesCompartilhadas?.length > 0 && (
                <div className="kanban-column__compartilhada-chain">
                  {col.entidadesCompartilhadas.map((ent, idx) => (
                    <React.Fragment key={`${ent.tipo}-${ent.id || idx}`}>
                      <span
                        className={`kanban-column__compartilhada-chain-item${
                          ent.tipo === entityType && String(ent.id) === String(entity?.id)
                            ? " kanban-column__compartilhada-chain-item--atual"
                            : ""
                        }`}
                      >
                        {ent.nome}
                      </span>
                      {idx < col.entidadesCompartilhadas.length - 1 && (
                        <span className="kanban-column__compartilhada-chain-sep">↔</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}

              {responsavelInfo && (
                <div
                  className={`kanban-column__responsavel${responsavelInfo.removido ? " kanban-column__responsavel--removido" : ""}`}
                  title={
                    responsavelInfo.removido
                      ? `Usuário removido do container — ${responsavelInfo.nickname || "Sem nome"}`
                      : undefined
                  }
                >
                  {responsavelInfo.removido ? (
                    <div className="kanban-column__responsavel-fallback kanban-column__responsavel-fallback--sombra">?</div>
                  ) : responsavelInfo.avatar_url ? (
                    <img src={responsavelInfo.avatar_url} alt={responsavelInfo.nickname} className="kanban-column__responsavel-avatar" />
                  ) : (
                    <div className="kanban-column__responsavel-fallback">{(responsavelInfo.nickname || "?")[0].toUpperCase()}</div>
                  )}
                  <span className="kanban-column__responsavel-name">
                    {responsavelInfo.removido ? "Removido" : (responsavelInfo.nickname || "Sem nome")}
                  </span>
                </div>
              )}

              {isDiarioObra ? (
                <div className="kanban-column__list kanban-column__list--rdo">
                  <div className="rdo-calendario-fixo">
                    <CalendarioDiarioObra
                      pilhaId={col.id}
                      usuarioId={usuarioId}
                      onSelectNota={openNotaById}
                      onMesChange={handleMesChangeRdo}
                    />
                  </div>
                  <Droppable droppableId={col.id} type="CARD" isDropDisabled={true}>
                    {(innerProvided) => (
                      <div
                        ref={innerProvided.innerRef}
                        {...innerProvided.droppableProps}
                        className="rdo-notas-scroll"
                      >
                        {carregandoRdoInfo ? (
                          <RdoNotasSkeleton />
                        ) : (
                          <>
                            {col.notas
                              .filter(n => {
                                if (n.tipo !== "Diário de Obra" || !n.data_entrega) return false;
                                const [ano, mes] = n.data_entrega.split("-").map(Number);
                                return ano === mesVisualizadoRdo.ano && mes === mesVisualizadoRdo.mes;
                              })
                              .sort((a, b) => new Date(b.data_entrega) - new Date(a.data_entrega))
                              .map((nota, idx) => {
                                const infoRdo = rdosInfoMap[nota.id];

                                return (
                                  <Draggable key={String(nota.id)} draggableId={String(nota.id)} index={idx} type="CARD" isDragDisabled={true}>
                                    {(prov, snap) => (
                                      <div className={`kcard kcard--rdo${snap.isDragging ? " dragging" : ""}`}
                                        ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                                        onClick={() => handleOpenNota(nota)}>
                                        <div className="kcard-rdo-row">
                                          <div className="kcard-rdo-mini-cal">
                                            <span className="kcard-rdo-mini-cal__dia-semana">{getDiaSemanaAbrev(nota.data_entrega)}</span>
                                            <span className="kcard-rdo-mini-cal__dia-numero">{getDiaNumero(nota.data_entrega)}</span>
                                            <span className="kcard-rdo-mini-cal__mes">{getMesAbrev(nota.data_entrega)}</span>
                                          </div>
                                          <div className="kcard-rdo-info">
                                            <span className="kcard__tag kcard__tag--rdo">
                                              <span className="material-symbols-outlined" style={{ fontSize: 12 }}>construction</span>
                                              Diário de obra
                                            </span>
                                            <span className="kcard-rdo-info__label">preenchido por</span>
                                            <div className="kcard-rdo-info__linha">
                                              <span className="kcard-rdo-info__nome">{infoRdo?.responsavel || "—"}</span>

                                              {infoRdo?.totalFotos > 0 && (
                                                <span
                                                  className="kcard__meta-item"
                                                  title={`${infoRdo.totalFotos} foto${infoRdo.totalFotos > 1 ? "s" : ""}`}
                                                >
                                                  <span className="material-symbols-outlined">image</span>
                                                  {infoRdo.totalFotos}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                          </>
                        )}
                        {innerProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              ) : (
                <Droppable droppableId={col.id} type="CARD">
                  {(innerProvided) => (
                    <div className="kanban-column__list" ref={innerProvided.innerRef} {...innerProvided.droppableProps}>
                      {col.notas
                        .filter(n => !(n.tipo === "Nota Rápida" && n.flutuante))
                        .map((nota, idx) => renderCard(nota, idx))}
                      {innerProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}
            </div>
          </div>
        )}
      </Draggable>
    </>
  );
}